import qrcode from "qrcode-generator";
import { code128Widths } from "../pages/items/common";

// Direct printing, the screen's half (docs/direct-printing.md): a document is a list of blocks, drawn with the
// browser's own text engine on a canvas at the printer's resolution, then reduced to black and white pixels. The
// browser shapes Arabic, joins its letters and orders mixed Arabic and English text (the Unicode bidirectional
// algorithm), so the printer and the agent need no Arabic support of their own: they receive pixels.

export type Align = "start" | "center" | "end";
/**
 * Page breaking (A4): `keep` keeps a block on the page of the block after it (a heading with its first row, a row with
 * its detail line); `withPrev` keeps it on the page of the block before it (a footer never sits alone on a page).
 */
export type Block = BlockBody & { keep?: boolean; withPrev?: boolean };
type BlockBody =
  | { t: "text"; text: string; size?: number; bold?: boolean; align?: Align; dir?: "rtl" | "ltr" }
  /** Columns that share the width by weight; each cell wraps on its own. */
  | { t: "row"; cells: { text: string; w: number; align?: Align; bold?: boolean; dir?: "rtl" | "ltr" }[]; size?: number; bold?: boolean }
  /** A label at the start and its value at the end of one line. */
  | { t: "kv"; k: string; v: string; bold?: boolean; size?: number }
  | { t: "rule"; dashed?: boolean }
  | { t: "space"; mm: number }
  | { t: "qr"; text: string; mm: number }
  | { t: "barcode"; text: string; mm: number; widthMm?: number }
  /** White text on a black band: COPY, NON-FISCAL RECEIPT. */
  | { t: "banner"; text: string; size?: number }
  /** A thick-bordered box of wrapped text, for notices the reader must not miss. */
  | { t: "box"; text: string; size?: number; bold?: boolean }
  /** A picture (the organization logo), centred, at most mm high and widthMm wide, its aspect ratio kept, dithered to black and white. */
  | { t: "image"; img: PrintImage; mm: number; widthMm: number };

/** A decoded picture and its size in pixels. */
export interface PrintImage { src: CanvasImageSource; w: number; h: number }

export interface PrintDoc {
  dir: "rtl" | "ltr"; blocks: Block[];
  /** Paged paper only: blocks repeated at the top of every page after the first (a report's column headings). */
  pageHead?: Block[];
  /** Paged paper only: a line at the foot of every page, given the page number and the page count. */
  pageFoot?: (page: number, pages: number) => string;
}
/** The paper as the agent mapping gives it: printable width, page height (null: a receipt as long as its content), dpi. */
export interface PaperSpec { widthMm: number; heightMm: number | null; dpi: number; marginMm?: number }
export interface Raster { width: number; height: number; data: string }

const FONT = '"Plex Arabic", "Segoe UI", Tahoma, sans-serif';
const BASE_PT = 8.5;

/** Waits for the web font in every weight the documents use, with Arabic and Latin text. */
export async function loadPrintFonts() {
  if (!("fonts" in document)) return;
  await Promise.all(["400", "600", "700"].flatMap((w) => [document.fonts.load(`${w} 24px ${FONT}`, "Delta دلتا 0123"), document.fonts.load(`${w} 24px ${FONT}`, "فاتورة")]));
}

interface Ctx { c: CanvasRenderingContext2D; dpi: number; width: number; margin: number; dir: "rtl" | "ltr" }
const px = (mm: number, dpi: number) => Math.round((mm / 25.4) * dpi);
const fontPx = (pt: number, dpi: number) => Math.round((pt / 72) * dpi);

function setFont(x: Ctx, pt: number, bold?: boolean, dir?: "rtl" | "ltr") {
  x.c.font = `${bold ? 700 : 400} ${fontPx(pt, x.dpi)}px ${FONT}`;
  x.c.direction = dir ?? x.dir;
}

/** Breaks text into lines no wider than max, at spaces; a word wider than the line is cut by characters. */
function wrap(x: Ctx, text: string, max: number): string[] {
  const out: string[] = [];
  for (const para of text.split("\n")) {
    let line = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (x.c.measureText(next).width <= max) { line = next; continue; }
      if (line) out.push(line);
      if (x.c.measureText(word).width <= max) { line = word; continue; }
      let part = "";
      for (const ch of word) { if (x.c.measureText(part + ch).width > max && part) { out.push(part); part = ch; } else part += ch; }
      line = part;
    }
    out.push(line);
  }
  return out;
}

/** The x of a text drawn in [left, left + w] with this alignment, in the canvas's direction. */
function place(x: Ctx, align: Align, left: number, w: number, dir: "rtl" | "ltr") {
  const startAtRight = dir === "rtl";
  if (align === "center") { x.c.textAlign = "center"; return left + w / 2; }
  const atRight = (align === "start") === startAtRight;
  x.c.textAlign = atRight ? "right" : "left";
  return atRight ? left + w : left;
}

/** Draws (when draw) or measures one block at y; returns its height. */
function block(x: Ctx, b: Block, y: number, draw: boolean): number {
  const inner = x.width - 2 * x.margin, left = x.margin, c = x.c;
  switch (b.t) {
    case "space": return px(b.mm, x.dpi);
    case "rule": {
      const h = Math.max(2, Math.round(x.dpi / 100)), pad = px(1.2, x.dpi);
      if (draw) {
        if (b.dashed) for (let i = left; i < left + inner; i += h * 4) c.fillRect(i, y + pad, Math.min(h * 2, left + inner - i), h);
        else c.fillRect(left, y + pad, inner, h);
      }
      return pad * 2 + h;
    }
    case "text": case "box": case "banner": {
      const pt = b.size ?? (b.t === "banner" ? BASE_PT + 1.5 : BASE_PT), bold = b.t === "banner" ? true : b.bold;
      const dir = b.t === "text" && b.dir ? b.dir : x.dir;
      setFont(x, pt, bold, dir);
      const pad = b.t === "text" ? 0 : px(1.5, x.dpi), border = b.t === "box" ? Math.max(3, Math.round(x.dpi / 70)) : 0;
      const lines = wrap(x, b.text, inner - 2 * (pad + border));
      const lh = Math.round(fontPx(pt, x.dpi) * 1.45), h = lines.length * lh + 2 * (pad + border);
      if (draw) {
        if (b.t === "banner") c.fillRect(left, y, inner, h);
        if (b.t === "box") { c.fillRect(left, y, inner, border); c.fillRect(left, y + h - border, inner, border); c.fillRect(left, y, border, h); c.fillRect(left + inner - border, y, border, h); }
        c.fillStyle = b.t === "banner" ? "#fff" : "#000";
        c.textBaseline = "middle";
        const align = b.t === "text" ? b.align ?? "start" : "center";
        lines.forEach((l, i) => c.fillText(l, place(x, align, left + pad + border, inner - 2 * (pad + border), dir), y + pad + border + i * lh + lh / 2));
        c.fillStyle = "#000";
      }
      return h;
    }
    case "kv": {
      const pt = b.size ?? BASE_PT;
      setFont(x, pt, b.bold);
      const lh = Math.round(fontPx(pt, x.dpi) * 1.45), gap = px(2, x.dpi);
      const vw = Math.min(c.measureText(b.v).width, inner * 0.62);
      const kLines = wrap(x, b.k, inner - vw - gap), vLines = wrap(x, b.v, inner * 0.62);
      const n = Math.max(kLines.length, vLines.length);
      if (draw) {
        c.textBaseline = "middle";
        kLines.forEach((l, i) => c.fillText(l, place(x, "start", left, inner, x.dir), y + i * lh + lh / 2));
        vLines.forEach((l, i) => c.fillText(l, place(x, "end", left, inner, x.dir), y + i * lh + lh / 2));
      }
      return n * lh;
    }
    case "row": {
      const pt = b.size ?? BASE_PT, gap = px(1.5, x.dpi), total = b.cells.reduce((a, k) => a + k.w, 0);
      const widths = b.cells.map((k) => ((inner - gap * (b.cells.length - 1)) * k.w) / total);
      const lh = Math.round(fontPx(pt, x.dpi) * 1.4);
      const wrapped = b.cells.map((k, i) => { setFont(x, pt, k.bold ?? b.bold, k.dir); return wrap(x, k.text, widths[i]!); });
      const n = Math.max(...wrapped.map((w) => w.length));
      if (draw) {
        c.textBaseline = "middle";
        // Columns run from the start side: right to left in Arabic.
        let pos = 0;
        b.cells.forEach((k, i) => {
          const w = widths[i]!, cellLeft = x.dir === "rtl" ? left + inner - pos - w : left + pos;
          setFont(x, pt, k.bold ?? b.bold, k.dir);
          wrapped[i]!.forEach((l, j) => c.fillText(l, place(x, k.align ?? "start", cellLeft, w, k.dir ?? x.dir), y + j * lh + lh / 2));
          pos += w + gap;
        });
      }
      return n * lh;
    }
    case "qr": {
      const q = qrcode(0, "M");
      q.addData(b.text, "Byte");
      q.make();
      const n = q.getModuleCount(), size = px(b.mm, x.dpi), cell = Math.max(1, Math.floor(size / (n + 2)));
      const total = cell * (n + 2), x0 = left + Math.round((inner - total) / 2);
      if (draw) for (let r = 0; r < n; r++) for (let k = 0; k < n; k++) if (q.isDark(r, k)) c.fillRect(x0 + (k + 1) * cell, y + (r + 1) * cell, cell, cell);
      return total;
    }
    case "image": {
      const scale = Math.min(px(b.mm, x.dpi) / b.img.h, Math.min(px(b.widthMm, x.dpi), inner) / b.img.w);
      const w = Math.max(1, Math.round(b.img.w * scale)), h = Math.max(1, Math.round(b.img.h * scale));
      if (draw) c.putImageData(dither(b.img, w, h), left + Math.round((inner - w) / 2), y);
      return h;
    }
    case "barcode": {
      const widths = code128Widths(b.text), modules = [...widths].reduce((a, w) => a + Number(w), 0) + 20;
      const want = px(b.widthMm ?? Math.min(60, (inner / x.dpi) * 25.4), x.dpi);
      // Whole dots per module, so every bar keeps its exact width ratio (a scanner needs that more than size).
      const unit = Math.max(1, Math.floor(want / modules)), h = px(b.mm, x.dpi), total = unit * modules;
      let pos = left + Math.round((inner - total) / 2) + unit * 10;
      if (draw) [...widths].forEach((w, i) => { const n = Number(w) * unit; if (i % 2 === 0) c.fillRect(pos, y, n, h); pos += n; });
      return h;
    }
  }
}

/**
 * A picture scaled to w × h on white (transparent parts become paper), in grey, then reduced to black and white dots
 * by error diffusion (Floyd–Steinberg), so mid tones and coloured logos stay readable on a one-colour printer.
 */
function dither(img: PrintImage, w: number, h: number): ImageData {
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  const c = cv.getContext("2d", { willReadFrequently: true })!;
  c.fillStyle = "#fff"; c.fillRect(0, 0, w, h);
  c.imageSmoothingQuality = "high";
  c.drawImage(img.src, 0, 0, w, h);
  const d = c.getImageData(0, 0, w, h), g = new Float32Array(w * h);
  for (let i = 0; i < g.length; i++) g[i] = d.data[i * 4]! * 0.299 + d.data[i * 4 + 1]! * 0.587 + d.data[i * 4 + 2]! * 0.114;
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) {
    const i = yy * w + xx, old = g[i]!, v = old < 128 ? 0 : 255, err = old - v;
    g[i] = v;
    if (xx + 1 < w) g[i + 1]! += err * 7 / 16;
    if (yy + 1 < h) { if (xx > 0) g[i + w - 1]! += err * 3 / 16; g[i + w]! += err * 5 / 16; if (xx + 1 < w) g[i + w + 1]! += err / 16; }
  }
  for (let i = 0; i < g.length; i++) { d.data[i * 4] = d.data[i * 4 + 1] = d.data[i * 4 + 2] = g[i]!; d.data[i * 4 + 3] = 255; }
  return d;
}

/** Loads a picture for printing; null when it cannot be had (no logo, offline, not an image). */
export async function loadPrintImage(url: string): Promise<PrintImage | null> {
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) return null;
    const blob = await res.blob();
    // A data: URL, which the page's content security policy allows for images (blob: is not).
    const dataUrl = await new Promise<string>((ok, fail) => { const f = new FileReader(); f.onload = () => ok(f.result as string); f.onerror = () => fail(f.error); f.readAsDataURL(blob); });
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    return img.naturalWidth && img.naturalHeight ? { src: img, w: img.naturalWidth, h: img.naturalHeight } : null;
  } catch { return null; }
}

/** Renders a document to pages. A receipt (heightMm null) is one page as long as its content. */
export function renderDoc(doc: PrintDoc, paper: PaperSpec): Raster[] {
  const width = Math.floor((paper.widthMm / 25.4) * paper.dpi / 8) * 8;
  const margin = px(paper.marginMm ?? (paper.heightMm ? 4 : 1), paper.dpi);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = 10;
  const x: Ctx = { c: canvas.getContext("2d", { willReadFrequently: true })!, dpi: paper.dpi, width, margin, dir: doc.dir };
  // Measure, group what must stay together (keep, withPrev), then fill pages group by group. A group taller than a
  // page starts a page of its own and is split block by block. Spacing at the top of a page is dropped, so no page
  // starts blank and none holds only spacing.
  const heights = doc.blocks.map((b) => block(x, b, 0, false));
  const pageH = paper.heightMm ? px(paper.heightMm, paper.dpi) : null;
  const head = pageH ? doc.pageHead ?? [] : [];
  const headH = head.reduce((a, b) => a + block(x, b, 0, false), 0);
  const footBlock = (i: number, n: number): Block => ({ t: "text", text: doc.pageFoot!(i, n), size: 7, align: "center" });
  const footH = pageH && doc.pageFoot ? block(x, footBlock(1, 1), 0, false) + px(2, paper.dpi) : 0;
  const avail = pageH ? pageH - 2 * margin - footH : Infinity;
  const groups: number[][] = [];
  doc.blocks.forEach((b, i) => {
    const prev = doc.blocks[i - 1];
    if (groups.length && (prev?.keep || b.withPrev)) groups[groups.length - 1]!.push(i);
    else groups.push([i]);
  });
  const pages: Block[][] = [[]], pageHeights: number[] = [0];
  const newPage = () => { pages.push([]); pageHeights.push(headH); };
  const add = (i: number) => {
    const cur = pages.length - 1, b = doc.blocks[i]!;
    if (b.t === "space" && !pages[cur]!.length) return;
    if (pageHeights[cur]! + heights[i]! > avail && pages[cur]!.length) { newPage(); if (b.t === "space") return; }
    pages[pages.length - 1]!.push(b);
    pageHeights[pageHeights.length - 1]! += heights[i]!;
  };
  for (const g of groups) {
    const gh = g.reduce((a, i) => a + heights[i]!, 0), cur = pages.length - 1;
    if (pageHeights[cur]! + gh > avail && pages[cur]!.length) newPage();
    g.forEach(add);
  }
  while (pages.length > 1 && !pages[pages.length - 1]!.some((b) => b.t !== "space")) { pages.pop(); pageHeights.pop(); }
  return pages.map((blocks, p) => {
    canvas.height = pageH ?? pageHeights[p]! + 2 * margin;
    const c = x.c;
    c.fillStyle = "#fff";
    c.fillRect(0, 0, canvas.width, canvas.height);
    c.fillStyle = "#000";
    let y = margin;
    if (p > 0) for (const b of head) y += block(x, b, y, true);
    for (const b of blocks) y += block(x, b, y, true);
    if (pageH && doc.pageFoot) block(x, footBlock(p + 1, pages.length), pageH - margin - footH + px(2, paper.dpi), true);
    return toRaster(c, canvas.width, canvas.height);
  });
}

/** Black and white, packed: 1 = black, most significant bit first, rows padded to bytes. */
export function toRaster(c: CanvasRenderingContext2D, width: number, height: number): Raster {
  const img = c.getImageData(0, 0, width, height).data, row = Math.ceil(width / 8), out = new Uint8Array(row * height);
  for (let y = 0; y < height; y++) for (let k = 0; k < width; k++) {
    const i = (y * width + k) * 4;
    // Luminance below 60 % counts as ink: keeps thin Arabic strokes without thickening Latin text.
    if (img[i]! * 0.299 + img[i + 1]! * 0.587 + img[i + 2]! * 0.114 < 153) out[y * row + (k >> 3)]! |= 0x80 >> (k & 7);
  }
  let s = "";
  for (let i = 0; i < out.length; i += 0x8000) s += String.fromCharCode(...out.subarray(i, i + 0x8000));
  return { width, height, data: btoa(s) };
}

/** A raster back on a canvas (the preview of what the printer gets, and the browser tests). */
export function rasterToCanvas(r: Raster): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = r.width; cv.height = r.height;
  const c = cv.getContext("2d")!, img = c.createImageData(r.width, r.height), bits = Uint8Array.from(atob(r.data), (ch) => ch.charCodeAt(0)), row = Math.ceil(r.width / 8);
  for (let y = 0; y < r.height; y++) for (let k = 0; k < r.width; k++) {
    const on = bits[y * row + (k >> 3)]! & (0x80 >> (k & 7)), i = (y * r.width + k) * 4;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = on ? 0 : 255; img.data[i + 3] = 255;
  }
  c.putImageData(img, 0, 0);
  return cv;
}
